import React, { useState, useEffect, useReducer } from 'react';

import AddIcon from '@material-ui/icons/Add';
import Skeleton from '@material-ui/lab/Skeleton';
import {
  addActionItemsAction,
  addNewActionItemAction,
  removeAllActionItemsAction,
  updateActionItemAction,
} from 'actions/actionItem';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ActionItemListReducer from 'reducers/actionItem';
import useAgendaSidePanelContext from 'screens/oneOnOne/levelUp/agendaSidePanel/context';
import { selectLoggedInEmployeeData, selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { StyledTypography, StyledGrid, StyledIconButton, StyledButton } from 'styles/common';

import { TASK_TYPES as ACTION_ITEM_STATUSES } from 'constants/ProjectConstants';
import { getQueryParams } from 'constants/helper';

import { createLevelUpTask, getLevelUpTasks, updateLevelUpTask } from 'services/levelUpTask';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography14 } from 'uiToolkit/Typography';

import useLevelUpSidePanelContext from '../../context';
import ActionItemDisplay from './ActionItemDisplay';
import CompletedActionItems from './CompletedActionItems';
import NoActiveActionItemsView from './NoActiveActionItemsView';

const ACTION_ITEMS = 'Action Items';
const INITIATIVES = 'Initiatives';

const ADD_ACTION_ITEM = 'Add action item';
const ADD_INITIATIVE = 'Add Initiative';

const NO_ACTIVE_ACTION_ITEM = 'Add action items that team member(s)';
const NO_ACTIVE_INITIATIVE = 'Nothing to see here yet.';

const CustomStyledIconButton = styled(StyledIconButton)`
  &.MuiIconButton-root.Mui-disabled {
    background-color: ${(props) => props.theme.palette.primary.main};
  }
  &.MuiButtonBase-root {
    width: 24px;
    height: 24px;
  }
`;

const CustomStyledAddActionItemButton = styled(StyledButton)`
  &: hover {
    background-color: ${(props) => props.theme.palette.background.lightBlue};
  }
`;

const StyledAddIcon = styled(AddIcon)`
  width: 16px;
  height: 16px;
`;

const ActionItemsContainer = styled.div`
  padding: 0.5rem;
  border-radius: 5px;
  font-size: 0.75rem;
  ${(props) =>
    props.disabled &&
    css`
      pointer-events: none;
      cursor: initial;
      opacity: 0.5;
    `};
`;

const AddButton = ({ onAddActionItem, buttonText = ADD_ACTION_ITEM }) => (
  <CustomStyledAddActionItemButton variant="text" onClick={onAddActionItem} padding="0.5rem 1rem">
    <CustomStyledIconButton circular color="primary" borderColor="primary" size="tinier" padding="xLarge">
      <StyledAddIcon />
    </CustomStyledIconButton>
    <StyledTypography variant="body2" color="primary" leftGutter={1.5} align="center" style={{ lineHeight: 'normal' }}>
      {buttonText}
    </StyledTypography>
  </CustomStyledAddActionItemButton>
);

const Header = ({ loading, onAddActionItem, isOneOnOne = false }) => {
  const heading = isOneOnOne ? ACTION_ITEMS : INITIATIVES;
  const buttonText = isOneOnOne ? ADD_ACTION_ITEM : ADD_INITIATIVE;
  return (
    <StyledGrid container justify="space-between" alignItems="center" bottomGutter={3}>
      <Typography14 weight={600}>{heading}</Typography14>
      {!loading && <AddButton buttonText={buttonText} onAddActionItem={onAddActionItem} />}
    </StyledGrid>
  );
};

const ActionItemsOrInitiatives = ({
  className,
  checkInInstanceId, // for create/update/delete api use this id.
  checkInParentId, // for get call api use this id.
  isDisabled,
  filterEmpIds,
  // below props are used only in levelUp sidepanel.
  levelUpCompetencyId = null,
  competencyId = null,
  levelUpId = null,
  // below props are used only in agenda sidepanel
  agendaId = null,
}) => {
  const history = useHistory();

  const { location } = history;
  const queryString = location.search;

  const { isOneOnOne } = getQueryParams(queryString);

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const {
    state: { reportee },
  } = useLevelUpSidePanelContext();

  const isForCompetency = Boolean(levelUpCompetencyId) || (Boolean(competencyId) && Boolean(levelUpId));

  const dispatch = useDispatch();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const {
    state: { closedCount: levelUpSidePanelClosed },
  } = useLevelUpSidePanelContext();

  const {
    state: { closedCount: agendaSidePanelClosed },
  } = useAgendaSidePanelContext();

  // const { page, hasMore, updatePagination, maxPageReached } = usePagination();

  const [allActionItems, actionItemsDispatch] = useReducer(ActionItemListReducer, []);

  // const prevActionItemsListLength = usePrevious(allActionItems?.length);

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const limit = 100;

  const getData = () => {
    // don't make get api call if there is no levelUpCompetencyId.
    // if (isForCompetency && !levelUpCompetencyId) return;

    setLoading(true);
    let apiCall;
    // const offset = page * limit;
    const offset = 0;

    apiCall = getLevelUpTasks.bind(null, {
      competencyId,
      levelUpId,
      limit,
      offset,
      employeeId: loggedInEmployeeId,
    });

    apiCall()
      .then((actionItems) => {
        actionItemsDispatch(addActionItemsAction(actionItems));
        // updatePagination({
        //   hasMore: actionItems.length === limit,
        //   page: page + 1,
        //   maxPageReached: page + 1
        // });
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Sorry! An error occurred while fetching action Items'));
        // updatePagination({ hasMore: false });
      })
      .finally(() => setLoading(false));
  };

  const addActionItem = (actionItem) => {
    setBusy(true);

    if (!actionItem.text) {
      actionItemsDispatch(updateActionItemAction(actionItem));
      setBusy(false);
      return;
    }

    createLevelUpTask({
      ...actionItem,
      checkInInstanceId,
      competencyId,
      levelUpId,
      employeeId: loggedInEmployeeId,
    })
      .then((newlyCreatedActionItem) => {
        newlyCreatedActionItem.isEditing = false;
        // Making sure that if this was a new agenda, it has a key of that one
        newlyCreatedActionItem.uiKey = actionItem.uiKey;
        // getData();
        actionItemsDispatch(updateActionItemAction(newlyCreatedActionItem));
      })
      .catch((err) =>
        dispatch(showErrorSnackbar(err.message || 'Sorry! An error occurred while creating the action Item'))
      )
      .finally(() => setBusy(false));
  };

  const handleUpdateActionItem = (actionItem) => {
    setBusy(true);

    if (!actionItem.text) {
      actionItemsDispatch(updateActionItemAction(actionItem));
      setBusy(false);
      return;
    }

    updateLevelUpTask({
      ...actionItem,
      dueTS: actionItem.dueTime?.timeStamp ? actionItem.dueTime?.timeStamp : null,
      checkInInstanceId,
      employeeId: loggedInEmployeeId,
    })
      .then((updateActionItem) => {
        updateActionItem.isEditing = false;
        updateActionItem.uiKey = actionItem.uiKey;
        actionItemsDispatch(updateActionItemAction(updateActionItem));
      })
      .catch((err) =>
        dispatch(showErrorSnackbar(err.message || 'Sorry! An error occurred while updating the action item'))
      )
      .finally(() => setBusy(false));
  };

  const createOrUpdate = (actionItem) => {
    if (!actionItem.uuid) {
      addActionItem(actionItem);
    } else {
      handleUpdateActionItem(actionItem);
    }
  };

  const onCheckToggle = async (actionItem) => {
    const isAlreadyCompleted = Boolean(actionItem.status === ACTION_ITEM_STATUSES.completed);

    createOrUpdate({
      ...actionItem,
      status: isAlreadyCompleted ? ACTION_ITEM_STATUSES.active : ACTION_ITEM_STATUSES.completed,
    });
  };

  const onAddActionItem = () => {
    // updatePagination({ page: 1 });
    // const employee = isForCompetency ? reportee : loggedInEmployee;
    const employee = loggedInEmployee;
    actionItemsDispatch(
      addNewActionItemAction(employee, {
        levelUpCompetencyId,
        agendaId,
      })
    );
  };

  // const visibleActionItems = allActionItems.slice((page - 1) * 5, 5 + (page - 1) * 5);

  // const hasMoreToDisplay = hasMore || allActionItems.length > 5 + (page - 1) * 5;

  const activeActionItems = allActionItems.filter((actionItem) => actionItem.status === ACTION_ITEM_STATUSES.active);
  const completedActionItems = allActionItems.filter(
    (actionItem) => actionItem.status === ACTION_ITEM_STATUSES.completed
  );

  // useEffect(() => {
  //   if (!checkInParentId) return;
  //   getData();
  // }, [checkInParentId]);

  useEffect(() => {
    // if (checkInParentId) {
    actionItemsDispatch(removeAllActionItemsAction());
    getData();
    // }
  }, [checkInParentId, agendaSidePanelClosed, levelUpSidePanelClosed]);

  // useEffect(() => {
  //   if (
  //     prevActionItemsListLength > allActionItems?.length &&
  //     allActionItems?.length / (limit - 1) <= page - 1
  //   ) {
  //     updatePagination({ page: page - 1 });
  //   }
  // }, [allActionItems?.length, page, limit, prevActionItemsListLength]);

  return (
    <ActionItemsContainer className={className} disabled={busy || isDisabled}>
      <>
        <Header isOneOnOne={isOneOnOne} onAddActionItem={onAddActionItem} loading={loading} />
        {loading && (
          <>
            <Skeleton width="40%" />
            <Skeleton width="50%" />
          </>
        )}
        {!loading && activeActionItems.length === 0 && (
          <NoActiveActionItemsView
            line1={isOneOnOne ? NO_ACTIVE_ACTION_ITEM : NO_ACTIVE_INITIATIVE}
            line2={
              isOneOnOne ? 'can track & deliver on' : 'Add initiatives and enable a better growth curve for yourself'
            }
          />
        )}

        {activeActionItems.map((actionItem, index) => (
          <ActionItemDisplay
            key={`agendaItem-${actionItem.uuid || `new${index}`}`}
            actionItem={actionItem}
            onCheckToggle={onCheckToggle}
            onUpdate={createOrUpdate}
            actionItemsDispatch={actionItemsDispatch}
            filterEmpIds={filterEmpIds}
          />
        ))}

        <CompletedActionItems
          actionItems={completedActionItems}
          onCheckToggle={onCheckToggle}
          onUpdate={createOrUpdate}
          actionItemsDispatch={actionItemsDispatch}
          filterEmpIds={filterEmpIds}
          isOneOnOne={isOneOnOne}
        />
      </>
    </ActionItemsContainer>
  );
};

export default ActionItemsOrInitiatives;
